import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOSchema from "@/components/feature/SEOSchema";
import { articleDetails } from "@/mocks/article";
import { articles } from "@/mocks/category";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleDetails[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-600 mb-6">该文章不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: "首页", path: "/" },
    { label: article.categoryName, path: `/category/${article.categoryId === "cat-1" ? "usage-guide" : article.categoryId === "cat-2" ? "latest-news" : article.categoryId === "cat-3" ? "faq-help" : article.categoryId === "cat-4" ? "security-tips" : "feature-intro"}` },
    { label: article.title },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    datePublished: article.publishDate,
    description: articles.find((a) => a.slug === article.slug)?.summary || "",
    url: `https://17c-yiqicao.com/article/${article.slug}`,
    publisher: {
      "@type": "Organization",
      name: "17c一起草官网入口",
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema schemas={[articleSchema]} />
      <Navbar />
      <main>
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full px-4 md:px-6 pt-6 pb-16">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-500">{article.publishDate}</span>
                <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-600">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-line text-accent-600 text-sm"></i>
                </div>
                <span className="font-medium">{article.author}</span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom">
              {article.sections.map((section, index) => {
                switch (section.type) {
                  case "heading":
                    const HeadingTag = `h${section.level || 2}` as keyof JSX.IntrinsicElements;
                    return (
                      <HeadingTag
                        key={index}
                        className={`font-heading font-bold text-foreground-950 mt-10 mb-4 ${
                          section.level === 2 ? "text-xl" : "text-lg"
                        }`}
                      >
                        {section.content}
                      </HeadingTag>
                    );

                  case "paragraph":
                    return (
                      <p
                        key={index}
                        className="text-sm text-foreground-700 leading-relaxed mb-4"
                      >
                        {section.content}
                      </p>
                    );

                  case "list":
                    return (
                      <ul key={index} className="mb-4 space-y-2">
                        {section.items?.map((item, i) => (
                          <li
                            key={i}
                            className="text-sm text-foreground-700 leading-relaxed flex items-start gap-2"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    );

                  case "tip":
                    return (
                      <div
                        key={index}
                        className="mb-6 p-4 rounded-xl bg-accent-50 border border-accent-200"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-lightbulb-line text-accent-600 text-xs"></i>
                          </div>
                          <p className="text-sm text-accent-800 leading-relaxed">
                            {section.content}
                          </p>
                        </div>
                      </div>
                    );

                  default:
                    return null;
                }
              })}
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">
                  相关文章推荐
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-100 rounded-xl p-5 hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <span className="text-xs text-primary-600 font-medium mb-2 block">
                        {ra.categoryName}
                      </span>
                      <h4 className="text-sm font-heading font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-500">{ra.publishDate}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}